import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Grid,
  Typography,
  Box,
  Modal,
  IconButton,
  ThemeProvider,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "antd";
import { DoctorconsoleGrid, StyledModal, StyledButton } from "../StyledComponents";
import {
  Checkbox,
  Grid as MuiGrid,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { toast } from "react-toastify";
import { CheckToken } from "../../login/AccessToken";
import { AttachmentsFilePreview } from "./AttachmentsFilePreview";
import QrCodeUploadByDoctor from "../QrCodeUploadByDoctor";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";

const guessAttachmentMimeType = (fileName, contentType) => {
  if (contentType && contentType !== "binary/octet-stream") {
    return contentType;
  }
  const lower = (fileName || "").toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
};

const fileBase64ToDataUrl = (file) => {
  const mimeType = guessAttachmentMimeType(file.file_name, file.content_type);
  if (!file?.file_base64) return null;
  const raw = String(file.file_base64).replace(/\s/g, "");
  if (raw.startsWith("data:")) return raw;
  return `data:${mimeType};base64,${raw}`;
};

const urlToDataUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to fetch file");
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getS3PreviewSrc = (file) => {
  const mimeType = guessAttachmentMimeType(file.file_name, file.content_type);
  if (mimeType.startsWith("image/")) {
    const dataUrl = fileBase64ToDataUrl(file);
    if (dataUrl) return dataUrl;
  }
  return file.url;
};

const Attachments = ({
  sectionName,
  activeSection,
  suggestions,
  aiSuggestionToggle,
  ruleSuggestionToggle,
  handleCrossButtonClick,
  data,
  setData,
  patientId,
  facilityName,
  orgCode,
  appMode,
  isS3ModalOpen,
  setIsS3ModalOpen
}) => {
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [attachments, setAttachments] = useState(data);
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  const Token = () => {
    const check = CheckToken();
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  };

  useEffect(() => {
    setAttachments(data);
  }, [data]);


  // const handleFileSelect = (acceptedFiles) => {
  //   Promise.all(
  //     acceptedFiles.map((file) => {
  //       return new Promise((resolve, reject) => {
  //         const reader = new FileReader();
  //         reader.onload = () => {
  //           resolve({
  //             name: file.name,
  //             type: file.type,
  //             size: file.size,
  //             base64: reader.result,
  //           });
  //         };
  //         reader.onerror = reject;
  //         reader.readAsDataURL(file);
  //       });
  //     })
  //   )
  //     .then((newAttachments) => {
  //       const formData = new FormData();
  //       formData.append("file_object", newAttachments);
  //       if (Token()) {
  //         axios
  //           .post(
  //             `${REACT_APP_BACKEND_ROUTE_URL}file_check_for_vulnerabilities/`,
  //             // formData,
  //             { file_object: newAttachments },
  //             {
  //               headers: headers,
  //             }
  //           )
  //           .then((res) => {
  //             console.log(res);
  //           })
  //           .catch((corruptError) => {
  //             console.error(
  //               "Error in file check for vulnerabilities:",
  //               corruptError
  //             );
  //           });
  //       }

  //       let newDetails = [...attachments, ...newAttachments];
  //       setAttachments(newDetails);
  //       setData(newDetails);
  //     })
  //     .catch((error) => {
  //       console.error("Error reading files:", error);
  //     });
  // };

  const handleFileSelect = (acceptedFiles) => {
    // Process files to extract base64 and raw data
    Promise.all(
      acceptedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              base64: reader.result, // Base64 string for UI
              rawFile: file, // Raw file for backend
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    )
      .then((fileDetails) => {
        // Create FormData and append all raw files
        const formData = new FormData();
        fileDetails.forEach((file) => {
          formData.append("file_object", file.rawFile); // Add each file
        });

        // Send files to the backend
        if (Token()) {
          axios
            .post(
              `${REACT_APP_BACKEND_ROUTE_URL}file_check_for_vulnerabilities/`,
              formData,
              {
                headers: {
                  ...headers,
                  "Content-Type": "multipart/form-data", // Required for file uploads
                },
              }
            )
            .then((res) => {
              console.log("Response:", res);
              if (res.data.status === "Success") {
                // Update state for UI (base64 data for previews, etc.)
                let newDetails = [...attachments, ...fileDetails];
                setAttachments(newDetails);
                setData(newDetails);
              } else if (res.data.status == "Failed") {
                if (res.data.message.includes("corrupted")) {
                  toast.error(
                    "The Attachment(s) uploaded is/are corrupted. Please try with a different document."
                  );
                } else if (res.data.message.includes("virus")) {
                  toast.error(
                    "The Attachment(s) uploaded has virus. Please try with a different document."
                  );
                } else {
                  toast.error(res.data.message);
                }
              }
            })
            .catch((error) => {
              console.error("Error in file check for vulnerabilities:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error processing files:", error);
      });
  };

  const handleRejectedFiles = (fileRejections) => {
    fileRejections.forEach((rejection) => {
      const { file, errors } = rejection;

      errors.forEach((error) => {
        if (error.code === "file-too-large") {
          toast.error(
            `The file "${file.name}" is too large. Maximum size allowed is 10 MB.`
          );
        } else if (error.code === "file-too-small") {
          toast.error(
            `The file "${file.name}" is too small. Minimum size allowed is 20 KB.`
          );
        } else if (error.code === "file-invalid-type") {
          toast.error(
            `The file "${file.name}" has an invalid file type. Accepted types are .pdf, .jpeg, .jpg, .png.`
          );
        }
      });
    });
  };
  const handleDeleteAttachment = (index) => {
    let newDetails = attachments.filter((_, ind) => index !== ind);
    console.log("Delete", index, newDetails);
    setAttachments(newDetails);
    setData(newDetails);
  };
  const [attachmentPreviewModalOpen, setAttachmentPreviewModalOpen] =
    useState(false);
  const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] =
    useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    }, // Specify MIME types and extensions // Accepted file types
    multiple: true,
    maxSize: 10 * 1024 * 1024, // 10MB
    minSize: 20 * 1024, // 20kb
    onDrop: handleFileSelect,
    onDropRejected: handleRejectedFiles,
  });
  const handleAttachmentPreviewModalOpen = (attachment) => {
    setAttachmentPreviewModalOpen(true);
    setSelectedAttachmentForPreview(attachment);
  };
  const closeAttachmentPreviewModal = () => {
    setAttachmentPreviewModalOpen(false);
    setSelectedAttachmentForPreview(null);
  };

  const [s3Files, setS3Files] = useState([]);
  const [s3SearchQuery, setS3SearchQuery] = useState("");
  const [isFetchingS3Docs, setIsFetchingS3Docs] = useState(false);
  const [isImportingS3, setIsImportingS3] = useState(false);
  const [selectedS3Files, setSelectedS3Files] = useState([]);

  const filteredS3Files = useMemo(() => {
    const q = s3SearchQuery.trim().toLowerCase();
    if (!q) return s3Files;
    return s3Files.filter((file) => {
      const name = (file.file_name || "").toLowerCase();
      const date = String(file.upload_date || "").toLowerCase();
      return name.includes(q) || date.includes(q);
    });
  }, [s3Files, s3SearchQuery]);

  const getUploadTimestamp = (file) => {
    const raw = file?.upload_date ?? file?.created_at ?? file?.uploaded_at;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const sortDocumentsByRecent = (documents) =>
    [...(documents || [])].sort(
      (a, b) => getUploadTimestamp(b) - getUploadTimestamp(a)
    );

  useEffect(() => {
    if (isS3ModalOpen && patientId) {
      fetchS3Documents();
    }
    if (!isS3ModalOpen) {
      setS3SearchQuery("");
    }
  }, [isS3ModalOpen, patientId]);

  const fetchS3Documents = async () => {
    setIsFetchingS3Docs(true);
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get-patient-uploaded-documents/`,
        {
          patient_id: patientId,
          upload_type: "doc",
          include_base64: true,
        },
        {
          headers: {
            "App-Mode": appMode || sessionStorage.getItem("APP_MODE") || "",
            "Org-Code": orgCode || sessionStorage.getItem("ORG_CODE") || "",
            "Username": sessionStorage.getItem("userName") || "",
            "User-Access-Key": sessionStorage.getItem("USER_ACCESS_KEY") || "",
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data?.status === "Success") {
        setS3Files(sortDocumentsByRecent(response.data.documents));
      } else {
        toast.error(response.data?.message || "Failed to fetch S3 documents.");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error(error.response?.data?.message || "Error fetching S3 documents.");
    } finally {
      setIsFetchingS3Docs(false);
    }
  };

  const handleToggleS3File = (file) => {
    setSelectedS3Files(prev =>
      prev.find(f => f.s3_key === file.s3_key)
        ? prev.filter(f => f.s3_key !== file.s3_key)
        : [...prev, file]
    );
  };

  const handleDownloadS3File = async (e, file) => {
    e.stopPropagation();
    const fileName = file.file_name || "document";

    try {
      const dataUrl = fileBase64ToDataUrl(file);
      if (dataUrl) {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (!file.url) {
        toast.error("Download URL not available");
        return;
      }

      const response = await fetch(file.url);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Could not download file. Please try again.");
    }
  };

  const handleImportS3Files = async () => {
    if (selectedS3Files.length === 0) return;

    setIsImportingS3(true);
    try {
      const formattedFiles = await Promise.all(
        selectedS3Files.map(async (file) => {
          const mimeType = guessAttachmentMimeType(
            file.file_name,
            file.content_type
          );
          let base64 = fileBase64ToDataUrl(file);
          if (!base64) {
            if (!file.url) {
              throw new Error(`No data available for ${file.file_name || "file"}`);
            }
            base64 = await urlToDataUrl(file.url);
          }
          return {
            name: file.file_name,
            type: mimeType,
            size: 0,
            base64,
          };
        })
      );

      const newDetails = [...attachments, ...formattedFiles];
      setAttachments(newDetails);
      setData(newDetails);
      setIsS3ModalOpen(false);
      setSelectedS3Files([]);
      toast.success(`${formattedFiles.length} files imported successfully!`);
    } catch (error) {
      console.error("Import S3 files failed:", error);
      toast.error("Failed to import selected files. Please try again.");
    } finally {
      setIsImportingS3(false);
    }
  };
  console.log("Attachments", attachments);

  return (
    <ThemeProvider theme={theme}>
      {/* Drag and Drop Area */}
      <Card
        sx={{
          borderRadius: "10px",
          boxShadow: "0 4px 4px 0 #C7C7CC inset",
          backgroundColor: "#FFF",
        }}
      >
        {activeSection && (
          <DoctorconsoleGrid
            item
            xs={12}
            sx={{
              marginTop: "5px",
              padding: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 2,
                mb: 2
              }}
            >
              <Box
                {...getRootProps()}
                sx={{
                  flex: 1,
                  padding: 3,
                  border: "2px dashed #21cdc0",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundImage: "url(/newImages/attachmentsBackground.svg)",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  width: "100%"
                }}
              >
                <input {...getInputProps()} />
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  Drag 'n' drop some files here, or click to select files
                </Typography>
              </Box>

              {/* QR Code Upload - Extreme Right */}
              <Box sx={{ ml: "auto" }}>
                <QrCodeUploadByDoctor
                  patientId={patientId}
                  orgCode={orgCode}
                  appMode={appMode}
                  facilityName={facilityName}
                />
              </Box>
            </Box>
          </DoctorconsoleGrid>
        )}

        {/* File Preview */}
        <AttachmentsFilePreview
          data={attachments}
          activeSection={activeSection}
          handleAttachmentPreviewModalOpen={handleAttachmentPreviewModalOpen}
          handleDeleteAttachment={handleDeleteAttachment}
          closeAttachmentPreviewModal={closeAttachmentPreviewModal}
          attachmentPreviewModalOpen={attachmentPreviewModalOpen}
          selectedAttachmentForPreview={selectedAttachmentForPreview}
        />
      </Card>

      {/* S3 Uploaded Documents Access Modal */}
      <StyledModal
        open={isS3ModalOpen}
        handleClose={() => setIsS3ModalOpen(false)}
        title="Browse Uploaded Documents"
      >
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by date or filename..."
            value={s3SearchQuery}
            onChange={(e) => setS3SearchQuery(e.target.value)}
            sx={{
              mb: 3,
              backgroundColor: '#fff',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />

          {isFetchingS3Docs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
              <CircularProgress />
            </Box>
          ) : s3Files.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#94a3b8', my: 6, fontStyle: 'italic' }}>
              No uploaded documents found for this patient.
            </Typography>
          ) : filteredS3Files.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#94a3b8', my: 6, fontStyle: 'italic' }}>
              No documents match your search.
            </Typography>
          ) : (
            <MuiGrid container spacing={2}>
              {filteredS3Files.map((file) => {
                const isSelected = !!selectedS3Files.find(f => f.s3_key === file.s3_key);
                const isPdf = file.file_name?.toLowerCase().endsWith(".pdf");

                return (
                  <MuiGrid item xs={6} sm={4} md={3} key={file.s3_key}>
                    <Card
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #21cdc0' : '1px solid #ddd',
                        transition: 'all 0.2s',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={() => handleToggleS3File(file)}
                    >
                      <Checkbox
                        checked={isSelected}
                        sx={{ position: 'absolute', top: 5, right: 5, color: '#21cdc0', '&.Mui-checked': { color: '#21cdc0' }, zIndex: 2 }}
                      />
                      
                      <Box
                        sx={{
                          flexGrow: 1,
                          height: 120,
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f5f5f5',
                          overflow: 'hidden',
                        }}
                      >
                        {isPdf ? (
                          <InsertDriveFileIcon sx={{ fontSize: 60, color: '#ff5252' }} />
                        ) : (
                          <img
                            src={getS3PreviewSrc(file)}
                            alt={file.file_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <IconButton
                          onClick={(e) => handleDownloadS3File(e, file)}
                          sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            backgroundColor: 'rgba(255,255,255,0.92)',
                            padding: '6px',
                            zIndex: 3,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                            '&:hover': { backgroundColor: '#fff' },
                          }}
                          size="small"
                          title="Download"
                        >
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ p: 1, borderTop: '1px solid #eee' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.file_name}>
                          {file.file_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {file.upload_date}
                        </Typography>
                      </Box>
                    </Card>
                  </MuiGrid>
                );
              })}
            </MuiGrid>
          )}


          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <StyledButton
              variant="contained"
              bgColor="#FF3B30"
              hoverBgColor="#b71c1c"
              onClick={() => setIsS3ModalOpen(false)}
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                px: 1.5,
                py: 0.6,
                minWidth: "90px",
              }}
            >
              Cancel
            </StyledButton>
            <StyledButton
              variant="contained"
              bgColor="#0E3F66"
              hoverBgColor="#07367dff"
              disabled={selectedS3Files.length === 0 || isImportingS3}
              onClick={handleImportS3Files}
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.8rem" },
                px: 1.6,
                py: 0.6,
                minWidth: "150px",
              }}
            >
              {isImportingS3
                ? "IMPORTING..."
                : `Import Selected (${selectedS3Files.length})`}
            </StyledButton>
          </Box>
        </Box>
      </StyledModal>
    </ThemeProvider>
  );
};

export default Attachments;
